import { fetchCurrentUser, RecipeItem } from '@library/api-user'
import { RecipeAvailabilityMap, useRecipeAvailability } from './useRecipeAvailability'
import useSWR from 'swr'
import { DayV2, fetchV2DaysForUser } from '@library/api-deliveries'
import { ApiKeys, LOCALE } from '../configs'
import { useState } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'

const formatTimestampDate = (dateString: string): string => {
  const date = new Date(dateString)
  const weekday = date.toLocaleDateString(LOCALE, { weekday: 'long' })
  const day = date.toLocaleDateString(LOCALE, { day: 'numeric' })
  const month = date.toLocaleDateString(LOCALE, { month: 'long' })
  return `${weekday} ${day} ${month}` // No comma after weekday
}

export type DeliveryLeadTime = {
  startTime: string
  endTime: string
  deliveryPrice: string
  coreSlotId: string
  defaultDay: number
  isSlotDefault: boolean
  date: string
}

const getLoadingFlag = (data: unknown, error: unknown): boolean => {
  return !data && !error
}

const getIsRecipeUnavailable = (
  recipeAvailabilityMap: RecipeAvailabilityMap | undefined,
  day: DayV2,
  recipeItems: RecipeItem[],
  orderNumPortion: string,
) => {
  if (!recipeAvailabilityMap) {
    return false
  }

  const numberOfPortions = parseInt(orderNumPortion)

  return day.daySlotLeadTimes.some((leadTime) => {
    return recipeItems.some((recipe) => {
      if (
        !recipeAvailabilityMap[leadTime.date] ||
        !recipeAvailabilityMap[leadTime.date][recipe.recipeUuid] ||
        !recipeAvailabilityMap[leadTime.date][recipe.recipeUuid][numberOfPortions]
      ) {
        return true
      }

      return recipeAvailabilityMap[leadTime.date][recipe.recipeUuid][numberOfPortions] === false
    })
  })
}

export const useDays = (
  recipeItems: RecipeItem[],
  orderNumPortion: string,
  periodWhenStart: string,
  periodWhenCutoff: string,
  daySlotLeadTimeId: string,
  orderType: string,
  deliveryDate: string,
) => {
  const [selectedDaySlotLeadTimeId, setSelectedDaySlotLeadTimeId] =
    useState<string>(daySlotLeadTimeId)
  const [selectedDeliveryDate, setDeliveryDate] = useState<string>(deliveryDate)

  const { data: userData, error: userError } = useSWR(ApiKeys.UserCurrent, fetchCurrentUser)

  const todaysDate = new Date().toISOString().split('T')[0]

  const hasUserData = Boolean(userData)

  const isOrderDateBeforeStartPeriod = !periodWhenStart || periodWhenStart >= todaysDate
  const isOrderDateBeforeCutoffPeriod = !periodWhenCutoff || periodWhenCutoff >= todaysDate

  const shouldFetchDays =
    hasUserData && isOrderDateBeforeStartPeriod && isOrderDateBeforeCutoffPeriod

  const { data: days, error: daysError } = useSWR(
    shouldFetchDays
      ? {
          filters: {
            cutoff_datetime_from: periodWhenStart,
            cutoff_datetime_until: periodWhenCutoff,
          },
          sort: 'date',
          direction: 'asc',
          postcode: userData?.user.shippingAddress.postcode,
          order_type: orderType,
        }
      : null,
    fetchV2DaysForUser,
  )

  const deliveryDateStart = days?.[0]?.date || ''
  const deliveryDateEnd = days?.[days?.length - 1]?.date || ''
  const { recipeAvailabilityMap, isLoading: isRecipeUnavailableLoading } = useRecipeAvailability(
    deliveryDateStart,
    deliveryDateEnd,
  )

  const initialState = {
    availableSlots: [],
    isLoading: false,
    selectedSlot: undefined,
    setSelectedDaySlotLeadTimeId,
    setDeliveryDate,
  }

  if (userError) {
    const errorMessage = `Error fetching user data ${JSON.stringify(userError)}`
    datadogLogs.logger.error(errorMessage)
    datadogRum.addError(userError, {
      page: 'MyGoustoPage',
      errorMessage: errorMessage,
    })

    return initialState
  }

  if (daysError) {
    const errorMessage = `Error fetching days data for ${userData?.user.id} ${JSON.stringify(
      daysError,
    )}`
    datadogLogs.logger.error(errorMessage)
    datadogRum.addError(daysError, {
      page: 'MyGoustoPage',
      errorMessage: errorMessage,
    })

    return initialState
  }

  const isLoading =
    getLoadingFlag(userData, userError) ||
    (shouldFetchDays && getLoadingFlag(days, daysError)) ||
    (shouldFetchDays && isRecipeUnavailableLoading)

  const availableSlots = days
    ?.map((day) => ({
      id: day.id,
      coreDayId: day.coreDayId,
      date: day.date,
      humanizedDate: formatTimestampDate(day.date),
      isRecipeUnavailable: getIsRecipeUnavailable(
        recipeAvailabilityMap,
        day,
        recipeItems,
        orderNumPortion,
      ),
      daySlotLeadTimes: day.daySlotLeadTimes
        .filter((leadTime) => {
          if (!leadTime.isSlotDefault) {
            return false
          }

          return true
        })
        .map((leadTime) => ({
          id: leadTime.id,
          isSelected: leadTime.id === selectedDaySlotLeadTimeId,
          startTime: leadTime.startTime,
          endTime: leadTime.endTime,
          deliveryPrice: leadTime.deliveryPrice,
          coreSlotId: leadTime.coreSlotId,
          defaultDay: leadTime.defaultDay,
        })),
    }))
    .filter((day) => {
      return day.daySlotLeadTimes.length > 0
    })

  const selectedSlot = availableSlots?.find((slot) => {
    return slot.date === selectedDeliveryDate
  })

  return {
    availableSlots,
    isLoading,
    selectedSlot,
    setSelectedDaySlotLeadTimeId,
    setDeliveryDate,
  }
}
